// NIGHT-DIRECTSTAFF-2026-10-22 §12: bulk actions on the selected leads -- Mark as test, Mark as spam, Add to view -- beside
// the existing Mark handled (HandledBulkBar) and Export CSV (GatherBar). Each mark asks first, naming the count; after it
// runs, Undo (this session) puts back exactly the leads it changed. Leads already in that state are left alone (and said).
// NIGHT-DIRECTSTAFF-2026-10-25b §1: + Assign (to a person; Undo puts each lead back to whoever had it), and every run and every Undo
// is logged once (logBulk -> a 'bulk_action' row the Activity page lists). Counts update live (the lists re-render from the rows).
import React, { useState } from 'react';
import { SUGGESTED, NAME_MAX, cleanName } from './lib/assign';

const btn = (bg, fg, off) => ({ background: bg, color: fg, borderRadius: 8, padding: '0 14px', minHeight: 44, fontSize: 13, fontWeight: 700, border: 0,
  cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1 });

/**
 * selected: the selected lead rows; testOn / spamOn: those marks are available (their columns exist);
 * isTest(l) / isSpam(l); mark(kind, ids, next) -> Promise<{ done: ids, failed: [{ id, error }] }>;
 * addToView(name, ids) -> Promise; confirmFn(text) -> bool (window.confirm by default); toast
 */
export default function BulkMarks({ selected = [], testOn = false, spamOn = false, isTest = () => false, isSpam = () => false, mark, addToView = null,
  confirmFn = null, toast = () => {}, assign = null, assigneeOf = () => '', names = SUGGESTED, logBulk = async () => true }) {
  const [busy, setBusy] = useState(false);
  const [undo, setUndo] = useState(null);       // { kind, ids, next } -- what the last mark changed
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [who, setWho] = useState('');
  const ask = confirmFn || ((m) => window.confirm(m));
  if (!selected.length && !undo) return null;
  const run = async (kind, next, rows, words) => {
    const ids = rows.map((l) => l.id);
    if (!ids.length) { toast(`All ${selected.length} selected are already ${words}.`); return; }
    if (!ask(`${next ? 'Mark' : 'Unmark'} ${ids.length} lead${ids.length === 1 ? '' : 's'} as ${words}?${rows.length < selected.length ? ` (${selected.length - rows.length} already are.)` : ''}`)) return;
    setBusy(true);
    try {
      const r = await mark(kind, ids, next);
      setUndo(r.done.length ? { kind, ids: r.done, next, words } : null);
      if (r.done.length) await logBulk(`${next ? 'marked' : 'unmarked'} as ${words}`, r.done);
      toast(r.failed.length ? `${r.done.length} marked, ${r.failed.length} not (${r.failed[0].error})` : `${r.done.length} marked as ${words}`);
    } finally { setBusy(false); }
  };
  const doUndo = async () => {
    if (!undo) return;
    setBusy(true);
    try {
      if (undo.kind === 'assign') {                       // each lead back to whoever had it before (or nobody)
        const groups = new Map(); for (const id of undo.ids) { const p = undo.prev.get(id) || ''; groups.set(p, [...(groups.get(p) || []), id]); }
        let done = 0; for (const [p, ids] of groups) { const r = await assign(ids, p); done += r.done.length; }
        await logBulk(`undid "assigned to ${undo.to}"`, undo.ids);
        toast(`Undone: ${done} put back`); setUndo(null); return;
      }
      const r = await mark(undo.kind, undo.ids, !undo.next);
      if (r.done.length) await logBulk(`undid "marked as ${undo.words}"`, r.done);
      toast(r.failed.length ? `Undone for ${r.done.length}; ${r.failed.length} could not be put back` : `Undone: ${r.done.length} put back`);
      setUndo(null);
    } finally { setBusy(false); }
  };
  const doAssign = async (e) => {
    e.preventDefault();
    const to = cleanName(who);
    if (!to || !assign) return;
    const rows = selected.filter((l) => assigneeOf(l) !== to);
    if (!rows.length) { toast(`All ${selected.length} selected are already assigned to ${to}.`); return; }
    if (!ask(`Assign ${rows.length} lead${rows.length === 1 ? '' : 's'} to ${to}?`)) return;
    setBusy(true);
    try {
      const prev = new Map(rows.map((l) => [l.id, assigneeOf(l)]));
      const r = await assign(rows.map((l) => l.id), to);
      if (r.done.length) { setUndo({ kind: 'assign', ids: r.done, prev, to, words: `assigned to ${to}` }); await logBulk(`assigned to ${to}`, r.done); }
      toast(r.failed.length ? `${r.done.length} assigned, ${r.failed.length} not (${r.failed[0].error})` : `${r.done.length} assigned to ${to}`);
      setAssigning(false); setWho('');
    } finally { setBusy(false); }
  };
  const saveView = async (e) => {
    e.preventDefault();
    if (!name.trim() || !addToView) return;
    setBusy(true);
    try { await addToView(name.trim().slice(0, 60), selected.map((l) => l.id)); toast(`Saved "${name.trim()}": these ${selected.length} leads, one tap away`); setNaming(false); setName(''); }
    catch (err) { toast(err.message || 'The view was not saved'); } finally { setBusy(false); }
  };
  return (
    <div data-testid="bulk-marks" className="card" style={{ padding: '10px 14px', marginBottom: 12, display: 'grid', gap: 8 }}>
      {selected.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{selected.length} selected:</span>
          {testOn && <button className="nb" data-testid="bulk-test" disabled={busy} onClick={() => run('test', true, selected.filter((l) => !isTest(l)), 'test')} style={btn('#ede9fe', '#5b21b6', busy)}>Mark as test</button>}
          {spamOn && <button className="nb" data-testid="bulk-spam" disabled={busy} onClick={() => run('spam', true, selected.filter((l) => !isSpam(l)), 'spam')} style={btn('#fee2e2', '#b91c1c', busy)}>Mark as spam</button>}
          {addToView && !naming && <button className="nb" data-testid="bulk-view" disabled={busy} onClick={() => setNaming(true)} style={btn('#f1f5f9', '#1d4ed8', busy)}>Add to view</button>}
          {assign && !assigning && <button className="nb" data-testid="bulk-assign" disabled={busy} onClick={() => setAssigning(true)} style={btn('#ecfeff', '#0e7490', busy)}>Assign</button>}
        </div>
      )}
      {assigning && (
        <form data-testid="bulk-assign-form" onSubmit={doAssign} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <label htmlFor="bulk-assign-who" style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Assign to</label>
          <input id="bulk-assign-who" data-testid="bulk-assign-who" className="input" list="bulk-assign-names" value={who} maxLength={NAME_MAX} onChange={(e) => setWho(e.target.value)}
            style={{ minHeight: 44, fontSize: 14, flex: '1 1 160px', minWidth: 0 }} />
          <datalist id="bulk-assign-names">{[...new Set([...names, ...SUGGESTED])].map((n) => <option key={n} value={n} />)}</datalist>
          <button className="nb" type="submit" data-testid="bulk-assign-save" disabled={busy || !cleanName(who)} style={btn('#0e7490', 'white', busy || !cleanName(who))}>Assign</button>
          <button className="nb" type="button" onClick={() => setAssigning(false)} style={btn('#f1f5f9', '#475569')}>Cancel</button>
        </form>
      )}
      {naming && (
        <form data-testid="bulk-view-form" onSubmit={saveView} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <label htmlFor="bulk-view-name" style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>View name</label>
          <input id="bulk-view-name" data-testid="bulk-view-name" className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} style={{ minHeight: 44, fontSize: 14, flex: '1 1 160px' }} />
          <button className="nb" type="submit" data-testid="bulk-view-save" disabled={busy || !name.trim()} style={btn('#1d4ed8', 'white', busy || !name.trim())}>Save view</button>
          <button className="nb" type="button" onClick={() => setNaming(false)} style={btn('#f1f5f9', '#475569')}>Cancel</button>
        </form>
      )}
      {undo && (
        <div data-testid="bulk-undo" role="status" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
          <span>{undo.ids.length} {undo.kind === 'assign' ? undo.words : `marked as ${undo.words}`}.</span>
          <button className="nb" data-testid="bulk-undo-btn" disabled={busy} onClick={doUndo} style={btn('#0f172a', 'white', busy)}>Undo</button>
          <button className="nb" onClick={() => setUndo(null)} style={btn('transparent', '#475569')}>Dismiss</button>
        </div>
      )}
    </div>
  );
}
